// API 共享类型（Phase 1 / S1 骨架）
//
// 设计依据：
//   - src/handlers/health.ts / src/handlers/me.ts 的返回体（schema: 'health/v1' / 'me/v1'）
//   - docs/architecture/backend-proposal.md §v3-J 真相源原则
//   - EP-010-S1：S1 仅 getHealth / getMe；S3 起扩 getSave/putSave，S5 起扩 restore/versions
//
// 本文件**只**定义类型 / 枚举 / 错误类，**不**做 fetch、不读不写 localStorage。
// 这是 §v3-J.2"localStorage 仅作为云端状态的只读 cache"的强约束边界 ——
// 类型层与运行时层一刀切：types.ts 不持有任何 IO 副作用。

// ───────────────────────────────────────────────────────────────────────────
// /health（§v3-G ① · §v3-H.4 首次探活目标）
// ───────────────────────────────────────────────────────────────────────────

export interface HealthResp {
  readonly ok: true;
  /** ISO 8601 UTC，例："2026-09-24T16:21:15.671Z" */
  readonly time: string;
  /** SAM Local = "local"；真云 = "dev" / "prod" */
  readonly stage: string;
  readonly service: 'dogtrip-api';
  /** 端点 schema 版本号，用于客户端做兼容性检查（S3 起扩字段时递增）*/
  readonly schema: 'health/v1';
}

// ───────────────────────────────────────────────────────────────────────────
// /me（§v3-G ① 末段 · S4 前 stub · S4 接 Cognito 后真解析 JWT）
// ───────────────────────────────────────────────────────────────────────────

/**
 * 标识 /me handler 是从哪个 authorizer 通道拿到 sub 的（调试用）。
 * S9 真云集成测阶段移除（S1 阶段保留便于端到端验证）。
 */
export type MeSource = 'jwt.claims' | 'lambda.authorizer' | 'bearer-stub';

export interface MeResp {
  /** 云端权威 userId（Cognito User Pool 的 `sub`）；S4 前可为 null（bearer-stub）*/
  readonly userId: string | null;
  readonly stage?: string;
  readonly source?: MeSource;
  readonly schema: 'me/v1';
}

export interface MeUnauthorized {
  readonly error: 'unauthenticated';
  readonly message: string;
  readonly schema: 'me/v1';
}

// ───────────────────────────────────────────────────────────────────────────
// /save · S3 起扩（§v3-C.4 端点表 GET/PUT /save；镜像 handler 端 SaveGetOk/SavePutOk/SavePutConflict）
// ───────────────────────────────────────────────────────────────────────────

/**
 * GET /save 命中（200）：返回完整当前档的"信封"（data 是原始 JSON 字符串，不做二次解析）。
 * 镜像 src/handlers/save-get.ts 的 SaveGetOk —— 两端字段一一对齐。
 */
export interface SaveGetOk {
  readonly schema: 'save/v1';
  readonly rev: number;
  /** 原始 SaveDataV1 JSON 字符串（§v3-B.3 整份 String 属性原则） */
  readonly data: string;
  /** 服务端权威 contentHash（§v3-D.1 #2 —— 前端永不计算 hash） */
  readonly contentHash: string;
  readonly schemaVersion: number;
  readonly sizeBytes: number;
  /** = DDB clientSavedAt；仅展示 / 诊断用，**不**作判据（§v3-D.1 #1） */
  readonly savedAt: number;
  /** 服务端 ISO-8601（§v3-B.2 #4） */
  readonly updatedAt: string;
  readonly updatedBy: string;
}

/** GET /save 404：云端无档（首次进入 / 迁移成功的语义分支） */
export interface SaveGetNotFound {
  readonly schema: 'save/v1';
  readonly kind: 'no_save';
  readonly message: string;
}

export type SaveGetResp = SaveGetOk | SaveGetNotFound;

/**
 * PUT /save 请求体形状（镜像 handler 端 SavePutReq）。
 *   - baseRev: 客户端知道的云端 rev（首次创建 = 0；后续 = 上次 GET 的 rev）
 *   - data: 整份 SaveDataV1 的 JSON 字符串（handler 重新解析计算 hash；**前端永远不传 hash**）
 *   - intent: 可选 'migrate' | 'normal'（仅诊断 / 日志用）
 *   - clientSavedAt: 可选（仅展示用；§v3-D.1 #1 不作判据）
 *   - clientId: 可选（写入 updatedBy 字段，便于诊断）
 */
export interface SavePutReq {
  readonly baseRev: number;
  readonly data: string;
  readonly intent?: 'migrate' | 'normal';
  readonly clientSavedAt?: number;
  readonly clientId?: string;
}

/** PUT /save 命中（200）：与 SaveGetOk 同形 —— 是"成功提交后的云端最新值" */
export type SavePutOk = SaveGetOk;

/** PUT /save 409 rev 冲突（§v3-B.4 Step 3 ①） */
export interface SavePutConflict {
  readonly error: 'rev_conflict';
  readonly message: string;
  readonly current: {
    readonly rev: number;
    readonly data: string;
    readonly contentHash: string;
    readonly schemaVersion: number;
    readonly sizeBytes: number;
    readonly savedAt: number;
    readonly updatedAt: string;
    readonly updatedBy: string;
  };
  readonly schema: 'save/v1';
}

export type SavePutResp = SavePutOk | SavePutConflict;

// ───────────────────────────────────────────────────────────────────────────
// 通用 API 错误形状（前端统一捕获用）
// ───────────────────────────────────────────────────────────────────────────

/**
 * 任何 4xx / 5xx / network failure 都包成 ApiError 抛出。
 * handler 的 401 body（{error: 'unauthenticated', ...}）也归到这一类。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body?: unknown;

  constructor(message: string, opts: { status: number; url: string; body?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.url = opts.url;
    this.body = opts.body;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// §v3-J 真相源原则：API client 与 localStorage 的边界
// ───────────────────────────────────────────────────────────────────────────

/**
 * **§v3-J 真相源原则硬约束**（backend-proposal.md §v3-J.1 / §v3-J.2）：
 *
 * 本工程 API client 是云端 DynamoDB 的**唯一出入口**。
 * 本文件（types.ts）与 src/api/client.ts **不**：
 *   - 读 localStorage 做状态判定
 *   - 写 localStorage 做状态变更
 *   - 把 localStorage 内容当作"狗在旅途 / 狗回来 / 揭晓"的依据
 *
 * 任何 dogState / trip / inventory 的判定路径都走"云端返回值 + Date.now()"
 * （§v3-J.2 硬纪律，EP-002 已有判定公式是本地缓存的镜像，**不**走 localStorage 重新判定）。
 *
 * localStorage 仅作为离线时的只读 cache（§v3-J.1 第 2 条），由 src/store/persist.ts
 * 唯一写盘；本 API client **不**碰 persist.ts，也**不**在 client 内调用 localStorage。
 *
 * S3 起 getSave/putSave 接入后，本文件**仍**不引入 localStorage 读写 —— 镜像写入
 * 走 src/store/persist.ts 的 store 订阅链，与 §v3-J.2 "本地 cache 只在 GET/PUT 成功后被动写入" 一致。
 */